import { useState, useEffect, useRef } from "react";
import swal from "sweetalert2";
import * as helper from "../global/helper";
import { uploadPublicFile } from "../api/fileAPI";
import { getTournamentLiveSettings, postTournamentSponsors, postTournamentLiveBackground } from "../api/tournamentAPI";

// Existing components in this codebase strip the '/Content/' URL prefix off an
// already-uploaded file right before re-saving it (see tournamentMaster.js) so the
// stored value is always just the filename. Mirrored here for Sponsor Image and for
// the Background Image/Video fields when a fresh file isn't picked.
const toStoredFileName = (url) => url ? url.split('/Content/')[1] : null;

const IMAGE_EXT = ['jpg', 'jpeg', 'png'];
const VIDEO_EXT = ['mp4', 'mov', 'wmv'];
const MAX_IMAGE_SIZE = 1024 * 1024 * 10;
const MAX_VIDEO_SIZE = 1024 * 1024 * 100;

const validatePickedFile = (file, kind) => {
    const ext = helper.getFileExt(file.name);
    const allowedExt = kind === 'video' ? VIDEO_EXT : IMAGE_EXT;
    if (!allowedExt.includes(ext)) {
        swal.fire({ icon: 'warning', titleText: 'File not supported!', html: `Only <strong>${kind}</strong> files are allowed for upload`, buttonsStyling: false, confirmButtonClass: 'btn btn-brand' });
        return false;
    }
    const maxSize = kind === 'video' ? MAX_VIDEO_SIZE : MAX_IMAGE_SIZE;
    if (file.size > maxSize) {
        swal.fire({ icon: 'warning', titleText: 'File size is too large!', html: `The file size is exceeding maximum limit of <strong>${kind === 'video' ? '100 MB' : '10 MB'}</strong>`, buttonsStyling: false, confirmButtonClass: 'btn btn-brand' });
        return false;
    }
    return true;
}

// Rectangle click-or-drop image/video picker. The preview itself is the drop target,
// so there's no separate "drop a file here" panel alongside it.
const ImageDropBox = ({ kind = 'image', previewUrl, onFile, height = 140, placeholder }) => {
    const inputRef = useRef(null);
    const [isDragOver, setIsDragOver] = useState(false);

    const pickFile = (file) => {
        if (!file) return;
        if (validatePickedFile(file, kind)) onFile(file);
    }

    const boxStyle = {
        height,
        border: `2px dashed ${isDragOver ? '#1a73e9' : '#c3c8d1'}`,
        borderRadius: '6px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        position: 'relative',
        background: isDragOver ? '#eef5ff' : '#fafafa',
    };

    return (
        <div
            style={boxStyle}
            onClick={() => inputRef.current && inputRef.current.click()}
            onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
            onDragLeave={() => setIsDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setIsDragOver(false); pickFile(e.dataTransfer.files && e.dataTransfer.files[0]); }}
        >
            {previewUrl ? (
                kind === 'video' ? (
                    <video src={previewUrl} muted controls style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                    <div style={{ width: '100%', height: '100%', backgroundImage: `url(${previewUrl})`, backgroundPosition: 'center', backgroundSize: 'cover' }} />
                )
            ) : (
                <div className="text-center text-muted" style={{ fontSize: '12px', padding: '10px' }}>
                    <i className={`la ${kind === 'video' ? 'la-video-camera' : 'la-picture-o'}`} style={{ fontSize: '22px', display: 'block', marginBottom: '4px' }}></i>
                    {placeholder || `Click or drop ${kind} here`}
                </div>
            )}
            <input
                ref={inputRef}
                type="file"
                hidden
                accept={kind === 'video' ? 'video/*' : 'image/*'}
                onChange={({ target }) => { pickFile(target.files && target.files[0]); target.value = ''; }}
            />
        </div>
    );
}

const TournamentLiveSettingsModal = (props) => {

    const { tournamentID } = props;

    const [sponsors,           setSponsors]           = useState([]);
    const [deletedSponsorIds,  setDeletedSponsorIds]  = useState([]);

    const [backgroundType,      setBackgroundType]     = useState('IMAGE');
    const [bgImageUrl,          setBgImageUrl]         = useState(null);
    const [bgImageBlob,         setBgImageBlob]        = useState(null);
    const [bgVideoUrl,          setBgVideoUrl]         = useState(null);
    const [bgVideoBlob,         setBgVideoBlob]        = useState(null);

    const [newSponsorName,     setNewSponsorName]      = useState('');
    const [newSponsorCaption,  setNewSponsorCaption]   = useState('');
    const [newSponsorBlob,     setNewSponsorBlob]      = useState(null);
    const [showAddSponsorModal, setShowAddSponsorModal] = useState(false);

    //Modal Show /Blur Other modals
    useEffect(() => {
        $("#tournamentLiveSettingsModal").modal({ backdrop: "static" });
        $("#tournamentLiveSettingsModal").on("hidden.bs.modal", function () { props.onDismissModal() });
        $("#tournamentLiveSettingsModal").modal("toggle");
    }, []);

    useEffect(() => {
        if (tournamentID) fetchLiveSettings();
    }, [tournamentID]);

    //Drag-to-reorder Sponsor rows (jQuery UI sortable, same plugin used for table-row
    //reordering elsewhere in the app, e.g. staggeredTemplate.js/matTemplate.js).
    //No `handle` option — the whole row is the drag target (see .sponsor-row CSS
    //below for the hover/grab cursor), not a separate handle column/icon.
    useEffect(() => {
        $('#tblSponsors tbody').sortable({
            stop: () => {
                const keys = [];
                $('#tblSponsors tbody > tr[data-key]').each((_, el) => keys.push($(el).attr('data-key')));
                setSponsors(prev => {
                    const byKey = new Map(prev.map(s => [String(s._key), s]));
                    return keys.map((key, i) => ({ ...byKey.get(String(key)), SortOrder: i }));
                });
            }
        });
    }, []);

    const applySponsors = (res) => {
        setSponsors((res.Sponsors || []).map((s, i) => ({ ...s, PendingBlob: null, _key: s.ID != null ? `id-${s.ID}` : `row-${i}` })));
        setDeletedSponsorIds([]);
    }

    const applyBackground = (res) => {
        const bg = res.Background || {};
        setBackgroundType(bg.BackgroundType || 'IMAGE');
        setBgImageUrl(bg.Image || null);
        setBgVideoUrl(bg.Video || null);
        setBgImageBlob(null);
        setBgVideoBlob(null);
    }

    // Initial load only — applies both sections, since nothing has been
    // locally edited yet at this point.
    const fetchLiveSettings = async () => {
        try {
            swal.fire({ showConfirmButton: false, onOpen: () => swal.showLoading(), titleText: 'Loading', text: 'Please Wait...', allowOutsideClick: false, allowEscapeKey: false });

            const res = await getTournamentLiveSettings(tournamentID);
            applySponsors(res);
            applyBackground(res);

            swal.close();
        } catch (err) {
            swal.fire({ icon: 'error', titleText: err.message, onOpen: () => swal.hideLoading() });
        }
    }

    // Post-save refreshers — each only applies its OWN section's response
    // data, so saving Sponsors never overwrites unsaved Background edits
    // (a picked-but-not-yet-saved image/video blob, an unsaved Active
    // toggle) and vice versa.
    const refreshSponsorsOnly = async () => {
        const res = await getTournamentLiveSettings(tournamentID);
        applySponsors(res);
    }

    const refreshBackgroundOnly = async () => {
        const res = await getTournamentLiveSettings(tournamentID);
        applyBackground(res);
    }

    //#region Sponsors -->

    const updateSponsorField = (index, field, value) => {
        setSponsors(prev => prev.map((s, i) => i === index ? { ...s, [field]: value } : s));
    }

    const onAddSponsorPhotoPick = (file) => setNewSponsorBlob(file);

    const removeSponsor = (index) => {
        const sponsor = sponsors[index];
        swal.fire({
            title: "Are you sure, you want to delete ?",
            showDenyButton: true, showCancelButton: true,
            confirmButtonText: "Yes", denyButtonText: `No`
        }).then((result) => {
            if (result.isConfirmed) {
                if (sponsor.ID != null) setDeletedSponsorIds(prev => ([...prev, sponsor.ID]));
                setSponsors(prev => prev.filter((_, i) => i !== index));
            }
        });
    }

    const addSponsor = () => {
        if (!newSponsorName) {
            swal.fire({ icon: 'warning', titleText: 'Kindly enter sponsor name', buttonsStyling: false, confirmButtonClass: 'btn btn-brand' });
            return;
        }
        if (!newSponsorBlob) {
            swal.fire({ icon: 'warning', titleText: 'Kindly select a sponsor photo', buttonsStyling: false, confirmButtonClass: 'btn btn-brand' });
            return;
        }

        setSponsors(prev => ([
            ...prev,
            {
                ID          : null,
                Name        : newSponsorName,
                Caption     : newSponsorCaption,
                Image       : null,
                PendingBlob : newSponsorBlob,
                SortOrder   : prev.length,
                Active      : 'Y',
                _key        : `new-${Date.now()}-${Math.random().toString(36).slice(2)}`,
            }
        ]));

        setNewSponsorName('');
        setNewSponsorCaption('');
        setNewSponsorBlob(null);
        setShowAddSponsorModal(false);
    }

    const saveSponsors = async () => {
        try {
            swal.fire({ titleText: 'Loading', text: 'Please Wait...', showConfirmButton: false, onOpen: () => swal.showLoading(), allowOutsideClick: false, allowEscapeKey: false });
            helper.StartProcessing($('#btnSaveSponsors'));

            const payload = [];
            for (const sponsor of sponsors) {
                let Image = sponsor.Image ? toStoredFileName(sponsor.Image) : null;
                if (sponsor.PendingBlob) {
                    const res = await uploadPublicFile(sponsor.PendingBlob, 'Content');
                    Image = res.fileName;
                }
                payload.push({
                    ID: sponsor.ID, Name: sponsor.Name, Image, Caption: sponsor.Caption,
                    SortOrder: sponsor.SortOrder, Active: sponsor.Active,
                });
            }

            await postTournamentSponsors(tournamentID, payload, deletedSponsorIds);
            await refreshSponsorsOnly();

            helper.StopProcessing($('#btnSaveSponsors'));
            swal.fire({ toast: true, icon: 'success', position: 'bottom-end', showConfirmButton: false, timer: 2000, titleText: 'Sponsors updated successfully' });
        } catch (err) {
            helper.StopProcessing($('#btnSaveSponsors'));
            swal.fire({ icon: 'error', buttonsStyling: false, confirmButtonClass: 'btn btn-brand', titleText: 'Error!', text: err.message });
        }
    }

    //#endregion

    //#region Background -->

    const saveBackground = async () => {
        try {
            swal.fire({ titleText: 'Loading', text: 'Please Wait...', showConfirmButton: false, onOpen: () => swal.showLoading(), allowOutsideClick: false, allowEscapeKey: false });
            helper.StartProcessing($('#btnSaveBackground'));

            let image = bgImageUrl ? toStoredFileName(bgImageUrl) : null;
            let video = bgVideoUrl ? toStoredFileName(bgVideoUrl) : null;

            if (bgImageBlob) {
                const res = await uploadPublicFile(bgImageBlob, 'Content');
                image = res.fileName;
            }
            if (bgVideoBlob) {
                const res = await uploadPublicFile(bgVideoBlob, 'Content');
                video = res.fileName;
            }

            await postTournamentLiveBackground(tournamentID, { image, video, backgroundType });
            await refreshBackgroundOnly();

            helper.StopProcessing($('#btnSaveBackground'));
            swal.fire({ toast: true, icon: 'success', position: 'bottom-end', showConfirmButton: false, timer: 2000, titleText: 'Background updated successfully' });
        } catch (err) {
            helper.StopProcessing($('#btnSaveBackground'));
            swal.fire({ icon: 'error', buttonsStyling: false, confirmButtonClass: 'btn btn-brand', titleText: 'Error!', text: err.message });
        }
    }

    //#endregion

    return (<>
        <div className="modal fade" id="tournamentLiveSettingsModal" tabIndex={-1} role="dialog" aria-hidden="true">
            <div className="modal-dialog modal-xl" role="document">
                <div className="modal-content">

                    <div className="modal-header">
                        <h5 className="modal-title"> Live Leaderboard Settings </h5>
                        <button type="button" className="close" data-dismiss="modal" aria-label="Close"></button>
                    </div>

                    <div className="modal-body">

                        <div className="row">

                            {/* Sponsors */}
                            <div className="col-6">
                                <div className="kt-portlet">
                                    <div className="kt-portlet__head">
                                        <div className="kt-portlet__head-label">
                                            <h3 className="kt-portlet__head-title">Sponsors</h3>
                                        </div>
                                        <div className="kt-portlet__head-toolbar">
                                            <button id="btnAddSponsor" type="button" className="btn btn-brand btn-icon-sm text-white d-inline" onClick={() => setShowAddSponsorModal(true)}><i className="la la-plus"></i> Add Sponsor</button>
                                        </div>
                                    </div>
                                    <div className="kt-portlet__body">
                                        <div className="sponsor-table-scroll">
                                            <table id="tblSponsors" className="table table-bordered table-hover margin-b0">
                                                <thead className="light-grey-background">
                                                    <tr>
                                                        <th style={{ width: '80px' }}>Photo</th>
                                                        <th>Name</th>
                                                        <th>Caption</th>
                                                        <th style={{ width: '80px' }}>Active</th>
                                                        <th style={{ width: '50px' }}></th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {sponsors.length === 0 ? (
                                                        <tr><td className="text-align-center" colSpan="5">No Sponsors</td></tr>
                                                    ) : sponsors.map((s, i) => {
                                                        const previewUrl = s.PendingBlob ? URL.createObjectURL(s.PendingBlob) : s.Image;
                                                        return (
                                                            <tr key={s._key} data-key={s._key} className="sponsor-row">
                                                                <td>
                                                                    <ImageDropBox kind="image" previewUrl={previewUrl} onFile={(f) => updateSponsorField(i, 'PendingBlob', f)} height={56} placeholder="" />
                                                                </td>
                                                                <td>
                                                                    <input type="text" className="form-control" value={s.Name || ''} onChange={({ target }) => updateSponsorField(i, 'Name', target.value)} />
                                                                </td>
                                                                <td>
                                                                    <input type="text" className="form-control" value={s.Caption || ''} onChange={({ target }) => updateSponsorField(i, 'Caption', target.value)} />
                                                                </td>
                                                                <td>
                                                                    <span className="kt-switch kt-switch--icon">
                                                                        <label>
                                                                            <input type="checkbox" checked={s.Active == 'Y'} onChange={({ target }) => updateSponsorField(i, 'Active', target.checked ? 'Y' : 'N')} />
                                                                            <span></span>
                                                                        </label>
                                                                    </span>
                                                                </td>
                                                                <td>
                                                                    <i className="la la-trash text-align-center text-danger" style={{ cursor: 'pointer', fontSize: '18px' }} onClick={() => removeSponsor(i)}></i>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                    <div className="kt-portlet__foot text-right">
                                        <button id="btnSaveSponsors" type="button" className="btn btn-brand" onClick={saveSponsors}>Save Sponsors</button>
                                    </div>
                                </div>
                            </div>

                            {/* Background */}
                            <div className="col-6 w-full">
                                <div className="kt-portlet">
                                    <div className="kt-portlet__head">
                                        <div className="kt-portlet__head-label">
                                            <h3 className="kt-portlet__head-title">Background</h3>
                                        </div>
                                    </div>
                                    <div className="kt-portlet__body">
                                        <div className="kt-radio-list mb-3">
                                            <label className="kt-radio mr-4">
                                                <input type="radio" name="backgroundType" checked={backgroundType === 'IMAGE'} onChange={() => setBackgroundType('IMAGE')} /> Show Image on Leaderboard
                                                <span></span>
                                            </label>
                                            <label className="kt-radio">
                                                <input type="radio" name="backgroundType" checked={backgroundType === 'VIDEO'} onChange={() => setBackgroundType('VIDEO')} /> Show Video on Leaderboard
                                                <span></span>
                                            </label>
                                        </div>
                                        <div className="row">
                                            <div className="col-6">
                                                <div className="d-flex align-items-center mb-2">
                                                    <label className="mb-0"><strong>Background Image</strong></label>
                                                    {backgroundType === 'IMAGE' && <span className="badge badge-success ml-2">Live</span>}
                                                </div>
                                                <ImageDropBox kind="image" previewUrl={bgImageBlob ? URL.createObjectURL(bgImageBlob) : bgImageUrl} onFile={setBgImageBlob} height={180} placeholder="Click or drop background image" />
                                            </div>
                                            <div className="col-6">
                                                <div className="d-flex align-items-center mb-2">
                                                    <label className="mb-0"><strong>Background Video</strong></label>
                                                    {backgroundType === 'VIDEO' && <span className="badge badge-success ml-2">Live</span>}
                                                </div>
                                                <ImageDropBox kind="video" previewUrl={bgVideoBlob ? URL.createObjectURL(bgVideoBlob) : bgVideoUrl} onFile={setBgVideoBlob} height={180} placeholder="Click or drop background video" />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="kt-portlet__foot text-right">
                                        <button id="btnSaveBackground" type="button" className="btn btn-brand" onClick={saveBackground}>Save Background</button>
                                    </div>
                                </div>
                            </div>

                        </div>

                    </div>

                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary margin-0" data-dismiss="modal"> Close </button>
                    </div>

                </div>
            </div>

            {/* Add Sponsor — plain overlay (not a separate jQuery-driven bootstrap
                modal), rendered INSIDE #tournamentLiveSettingsModal's own DOM
                subtree. Bootstrap's modal focus-trap (_enforceFocus) refocuses
                the outer modal whenever focus lands outside its element, so this
                must be a descendant of it or the Sponsor Name/Caption inputs
                lose focus on every keystroke. */}
            {showAddSponsorModal && (
                <>
                    <div className="modal-backdrop fade show" style={{ zIndex: 1060 }} onClick={() => setShowAddSponsorModal(false)} />
                    <div className="modal fade show" style={{ display: 'block', zIndex: 1070 }} tabIndex={-1} role="dialog" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-dialog modal-dialog-centered" role="document">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">Add Sponsor</h5>
                                    <button type="button" className="close" aria-label="Close" onClick={() => setShowAddSponsorModal(false)}><span aria-hidden="true">&times;</span></button>
                                </div>
                                <div className="modal-body">
                                    <ImageDropBox kind="image" previewUrl={newSponsorBlob ? URL.createObjectURL(newSponsorBlob) : null} onFile={onAddSponsorPhotoPick} height={160} placeholder="Click or drop sponsor image" />
                                    <input type="text" className="form-control mt-3" placeholder="Sponsor Name" value={newSponsorName} onChange={({ target }) => setNewSponsorName(target.value)} />
                                    <input type="text" className="form-control mt-2" placeholder="Caption" value={newSponsorCaption} onChange={({ target }) => setNewSponsorCaption(target.value)} />
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary margin-0" onClick={() => setShowAddSponsorModal(false)}>Cancel</button>
                                    <button type="button" className="btn btn-brand" onClick={addSponsor}>Add Sponsor</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>

        <style>{`
            /* Whole row is the drag target — grab cursor only appears on
               hover, no dedicated handle icon/column needed. */
            #tblSponsors tbody tr.sponsor-row {
                cursor: grab;
            }
            #tblSponsors tbody tr.sponsor-row:active {
                cursor: grabbing;
            }
            .ui-sortable-helper {
                display: table;
            }
            /* Cap the sponsor list's height so a long list scrolls inside
               the portlet instead of growing the modal. */
            .sponsor-table-scroll {
                max-height: 340px;
                overflow-y: auto;
            }
            .sponsor-table-scroll table {
                margin-bottom: 0;
            }
            .sponsor-table-scroll thead th {
                position: sticky;
                top: 0;
                z-index: 1;
            }
        `}</style>
    </>);
}

export default TournamentLiveSettingsModal;